import type { User } from '@/@types/user'
import Page from '@/components/Page'
import { Tab } from '@/components/Sidebar/config'
import UserProfile from '@/components/UserProfile'
import { FileRouteTypes } from '@/routeTree.gen'
import { getUserQueryOptions } from '@/utils/fetchers/user'
import useTabClickListener from '@/utils/hooks/useTabClickListener'
import { useCurrentUser } from '@/utils/hooks/useUser'
import queryClient from '@/utils/queryClient'
import { css } from '@emotion/react'
import { Tabs } from '@ruguoapp/jike-fe-components'
import type { TabsDataItem } from '@ruguoapp/jike-fe-components/dist/Tabs'
import { type UseSuspenseQueryOptions, useSuspenseQuery } from '@tanstack/react-query'
import { Link, Outlet, createFileRoute, useChildMatches } from '@tanstack/react-router'
import { compact } from 'lodash-es'
import toast from 'react-hot-toast'
import { useTitle } from 'react-use'

export const Route = createFileRoute('/u/$username')({
	component: UserProfilePage,
	loader: ({ params: { username } }) => queryClient.ensureQueryData(getUserQueryOptions(username)),
})

export default function UserProfilePage() {
	const childRoute = useChildMatches()[0]
	const { username } = Route.useParams()
	const currentUser = useCurrentUser()
	const isMyUserProfilePage = username === currentUser?.username
	const { data: user, refetch } = useSuspenseQuery(
		getUserQueryOptions(username) as UseSuspenseQueryOptions<User>
	)

	const tabs: TabsDataItem<FileRouteTypes['id']>[] = compact([
		{
			label: (
				<Link
					key="post"
					to="/u/$username"
					params={{
						username,
					}}
				>
					动态
				</Link>
			),
			value: '/u/$username/',
		},
		isMyUserProfilePage && {
			label: (
				<Link
					key="collection"
					to="/u/$username/collection"
					params={{
						username,
					}}
				>
					收藏
				</Link>
			),
			value: '/u/$username/collection',
		},
		isMyUserProfilePage && {
			label: (
				<Link
					key="topic"
					to="/u/$username/topic"
					params={{
						username,
					}}
				>
					我的圈子
				</Link>
			),
			value: '/u/$username/topic',
		},
	])

	useTabClickListener(Tab.USER_PROFILE, () => {
		toast.promise(refetch(), {
			loading: '加载中...',
			success: '加载成功',
			error: '加载失败',
		})
	})

	useTitle(`${isMyUserProfilePage ? '我的主页' : `${user.screenName}的主页`} - 即刻`)

	if (!user) {
		return null
	}

	return (
		<Page
			isHeaderShow
			isImmersiveHeader
			css={css`
				min-height: 100vh;
			`}
		>
			<div
				css={css`
					display: flex;
					flex-direction: column;
					gap: 20px;
				`}
			>
				<UserProfile data={user} />
				<section>
					<Tabs<FileRouteTypes['id']>
						value={childRoute.routeId as FileRouteTypes['id']}
						data={tabs}
						css={css`
							.jk-tabs {
								&-item {
									padding: unset;
									a {
										display: block;
										text-align: center;
										min-width: 96px;
										padding: 10px var(--padding-horizontal);
									}
								}

								&-indicator {
									width: 100%;
								}
							}
						`}
					/>

					<Outlet />
				</section>
			</div>
		</Page>
	)
}
